import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { OverlayTrigger, Popover, Glyphicon } from 'react-bootstrap';

import cloudinary from 'cloudinary-core';

import axios from 'axios';

import { updateLocalBrowserStats } from '../../actions/CdnActions';

const fileFormat = 'jpg';
const cl = cloudinary.Cloudinary.new();
cl.fromDocument();

const toKey = (path, cdnKey) => `${path}-${cdnKey}`;
const getUrl = (publicId, transformation) => cl.url(publicId, { transformation, format: fileFormat });
// See API for PerformanceResourceTiming here - https://developer.mozilla.org/en-US/docs/Web/API/PerformanceResourceTiming
const getPerformanceTiming = performanceResourceTiming => performanceResourceTiming.responseEnd - performanceResourceTiming.requestStart;

const numSamples = 11;

const getInitialState = function() {
    return {
        data: []
    };
};

class LocalDataPoint extends Component {

    constructor(props) {
        super(props);
        this.activeRequest = getInitialState();
    }

    executeSingleRequest(path) {
        const salt = Math.random();
        return axios.get(path, {
            params: {
                salt
            }
        })
        .then(
            response => {
                const url = response.request.responseURL;
                const resourceTiming = window.performance.getEntriesByName(url)[0];

                // Sanity check that the origin responded with timing data - https://www.stevesouders.com/blog/2014/08/21/resource-timing-practical-tips/
                return resourceTiming.requestStart
                  ? getPerformanceTiming(resourceTiming)
                  : console.log('Got partial timing data', resourceTiming.toJSON());
            }
        )
        .catch(err => console.error('Failed to get performance timing', err.message || err.response.data));
    }

    fetchData(path, requestKey) {
        const CancelToken = axios.CancelToken;
        const source = CancelToken.source();
        this.activeRequest[requestKey] = source;

        const requestChain = [];
        for (let i =0; i < numSamples; i++) {
            requestChain.push(i);
        }
        // Execute requests serially
        const lastPromise = requestChain.reduce(
            (previousPromise, currentValue, index) =>
                previousPromise.then(() => this.executeSingleRequest(path)
                .then(performanceTiming =>
                    performanceTiming ?
                    this.activeRequest.data.push(performanceTiming) : undefined
                ))
            , this.executeSingleRequest(path)); // First execution is warmup and is not added to results

        lastPromise.then(() => {
            this.props.updateLocalBrowserStats(path, this.props.cdnKey, this.activeRequest.data);
            this.activeRequest = getInitialState();
        });
    }

    shouldComponentUpdate(nextProps, nextState) {
        const isSameProvider = this.props.cdnKey === nextProps.cdnKey;
        const isPathDefined = this.props.path || nextProps.path;
        // const isSamePath = this.props.path === nextProps.path;
        return isSameProvider && isPathDefined;
    }

    componentWillMount() {
        // Start fetching data for initial render
        this.fetchData(this.props.path, toKey(this.props.path, this.props.cdnKey));
    }

    componentWillReceiveProps(nextProps) {
        const { cdnStats, path, cdnKey } = nextProps;
        const requestKey = toKey(path, cdnKey);
        if (!this.shouldComponentUpdate(nextProps)) {
            // Irrelevant props
            return;
        }
        if (cdnStats[requestKey]) {
            // Data for this data point has already been calculated - use it
            return;
        } else if (this.activeRequest[requestKey]) {
            // Currently fetching data for this point - nothing to do here
            return;
        } else {
            // Need to fetch data for this data point
            this.fetchData(path, requestKey);
        }
    }

    render () {
        const { cdnStats, path, cdnKey } = this.props;

        const key = toKey(path, cdnKey);
        if (!cdnStats[key]) {
            // Waiting for data - nothing to show
            return <div className="loading-pulse" />;
        } else if (cdnStats[key].err) {
            return (
                <div>N/A</div>
            );
        } else {
            const style = cdnStats[key].isFastest ? { color: 'green', fontWeight: 'bold' } : {};
            const popoverHoverFocus = (
                <Popover id="popover-trigger-hover-focus" title="Statistics">
                    <div><strong>Average</strong>: {cdnStats[key].mean.toFixed(2)}ms</div>
                    <div><strong>Median</strong>: {Math.ceil(cdnStats[key].median)}ms</div>
                    <div><strong>P10</strong>: {Math.ceil(cdnStats[key].p10)}ms</div>
                    <div><strong>P90</strong>: {Math.ceil(cdnStats[key].p90)}ms</div>
                    {
                        process.env.NODE_ENV !== 'production' ? <div><strong>Iterations</strong>: {cdnStats[key].iterations}</div> : null
                    }
                </Popover>
            );
            return (
                <div style={style}>
                    <OverlayTrigger style="cursor: pointer" trigger={['hover', 'focus']} placement="left" overlay={popoverHoverFocus}>
                        <span>
                            <div className="info-icon"/>
                            {Math.ceil(cdnStats[key].median)}ms
                        </span>
                    </OverlayTrigger>
                </div>
            );
        }
    }
}

LocalDataPoint.propTypes = {
    cdnStats: PropTypes.object.isRequired,
    path: PropTypes.string.isRequired,
    cdnKey: PropTypes.string.isRequired
};

function mapStateToProps(state) {
    return {
        cdnStats: state.cdnStats
    };
}

function mapDispatchToProps(dispatch) {
    return {
        updateLocalBrowserStats: (path, cdnKey, stats) => dispatch(updateLocalBrowserStats(path, cdnKey, stats))
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(LocalDataPoint);



/** WEBPACK FOOTER **
 ** ./src/js/components/MultiCdn/LocalDataPoint.js
 **/